import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import type { LibraryItem, Tag, RichContent } from "@/types";
import { ArrowLeft, ExternalLink, PlayCircle, FileText, Calendar, Tag as TagIcon, Video, Sparkles, AlertCircle, Clock, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useResourceEmbedding } from "@/hooks/useResourceEmbedding";
import RichContentSection from "@/components/library/RichContentSection";
import ToolExpertsSection from "@/components/library/ToolExpertsSection";
import { toast } from "sonner";

// Check if item has Show & Tell tag
const hasShowAndTellTag = (tags: Tag[] | undefined): boolean => {
  return tags?.some(t => t.name === "Show & Tell") || false;
};

interface LibraryItemWithTags extends LibraryItem {
  resourceTags?: Tag[];
  meta_title?: string | null;
  meta_description?: string | null;
  og_title?: string | null;
  og_description?: string | null;
  json_ld?: Record<string, unknown> | null;
  source_files?: string[] | null;
}

interface SimilarResource {
  id: string;
  title: string;
  description: string;
  type: string;
  slug: string;
  thumbnail_url: string | null;
  similarity: number;
}

const LibraryItemDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { organization, isLoading: orgLoading } = useOrganization();
  const [item, setItem] = useState<LibraryItemWithTags | null>(null);
  const [relatedItems, setRelatedItems] = useState<LibraryItemWithTags[]>([]);
  const [similarItems, setSimilarItems] = useState<SimilarResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportContext, setReportContext] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);
  const [loadingFile, setLoadingFile] = useState<string | null>(null);
  const { getSimilarResources } = useResourceEmbedding();

  // Check if theme is light (for prose classes)
  const isLightTheme = organization?.settings?.background_color 
    ? parseFloat(organization.settings.background_color.split(' ')[2]) > 50 
    : false;

  useEffect(() => {
    if (!orgLoading && organization?.id) {
      loadItem();
    }
  }, [slug, orgLoading, organization?.id]);

  // Track view count
  useEffect(() => {
    if (!item?.id) return;
    
    // Prevent counting same session multiple times
    const viewedKey = `viewed_${item.id}`;
    if (sessionStorage.getItem(viewedKey)) return;
    
    supabase.rpc('increment_view_count', { item_id: item.id }).then(() => {
      sessionStorage.setItem(viewedKey, 'true');
    });
  }, [item?.id]);

  const loadItem = async () => {
    if (!slug || !organization?.id) return;
    
    setLoading(true);
    
    // Fetch the main item with organization filter
    const { data: itemData, error: itemError } = await supabase
      .from('library_items')
      .select('*')
      .eq('slug', slug)
      .eq('organization_id', organization.id)
      .eq('is_published', true)
      .maybeSingle();

    if (itemData && !itemError) {
      // Fetch tags for this item
      const { data: tagsData } = await supabase
        .from('resource_tags')
        .select('tag_id, tags(*)')
        .eq('resource_id', itemData.id);

      const resourceTags = tagsData?.map((rt: any) => rt.tags as Tag).filter(Boolean) || [];
      
      setItem({ ...itemData, resourceTags } as LibraryItemWithTags);
      
      // Try to get similar items via vector similarity
      const similar = await getSimilarResources(itemData.id);
      if (similar.length > 0) {
        setSimilarItems(similar.slice(0, 6));
        setRelatedItems([]); // Don't show tag-based related if we have vector similar
      } else {
        // Fallback to tag-based related items
        setSimilarItems([]);
        await loadTagBasedRelated(itemData.id, resourceTags);
      }
    }
    
    setLoading(false);
  };

  const loadTagBasedRelated = async (itemId: string, resourceTags: Tag[]) => {
    const tagIds = resourceTags.map(t => t.id);
    let relatedData: LibraryItem[] = [];
    
    if (tagIds.length > 0) {
      const { data: relatedTagData } = await supabase
        .from('resource_tags')
        .select('resource_id')
        .in('tag_id', tagIds)
        .neq('resource_id', itemId);

      const relatedIds = [...new Set(relatedTagData?.map(rt => rt.resource_id) || [])].slice(0, 3);
      
      if (relatedIds.length > 0) {
        const { data } = await supabase
          .from('library_items')
          .select('*')
          .in('id', relatedIds)
          .eq('is_published', true);
        relatedData = data as LibraryItem[] || [];
      }
    }
    
    // If not enough related items from tags, fetch any other items
    if (relatedData.length < 3) {
      const { data: fallbackData } = await supabase
        .from('library_items')
        .select('*')
        .eq('is_published', true)
        .neq('id', itemId)
        .limit(3 - relatedData.length);
      
      if (fallbackData) {
        relatedData = [...relatedData, ...(fallbackData as LibraryItem[])];
      }
    }

    // Fetch tags for related items
    const relatedIds = relatedData.map(r => r.id);
    if (relatedIds.length > 0) {
      const { data: relatedTagsData } = await supabase
        .from('resource_tags')
        .select('resource_id, tag_id, tags(*)')
        .in('resource_id', relatedIds);

      const tagsMap = new Map<string, Tag[]>();
      for (const rt of relatedTagsData || []) {
        const existing = tagsMap.get(rt.resource_id) || [];
        if (rt.tags) existing.push(rt.tags as Tag);
        tagsMap.set(rt.resource_id, existing);
      }

      const relatedWithTags = relatedData.map(item => ({
        ...item,
        resourceTags: tagsMap.get(item.id) || []
      }));

      setRelatedItems(relatedWithTags);
    } else {
      setRelatedItems([]);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <PlayCircle className="h-6 w-6" />;
      default:
        return <FileText className="h-6 w-6" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleReportOutdated = async () => {
    if (!item) return;
    
    setSubmittingReport(true);
    try {
      const { error } = await supabase
        .from('pending_resources')
        .insert({
          submission_type: 'outdated_report',
          linked_resource_id: item.id,
          urls: [item.content_url],
          text_input: reportContext || `User reported that "${item.title}" may have outdated content.`,
          status: 'ready_for_review'
        });

      if (error) throw error;

      toast.success('Thank you! Your report has been submitted for review.');
      setReportDialogOpen(false);
      setReportContext('');
    } catch (error: any) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmittingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 px-4 max-w-4xl mx-auto">
        <Skeleton className="h-10 w-32 mb-8" />
        <Skeleton className="h-12 w-3/4 mb-4" />
        <Skeleton className="h-24 w-full mb-8" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen py-12 px-4 max-w-4xl mx-auto">
        <Link to="/library">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Library
          </Button>
        </Link>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4">Resource Not Found</h2>
          <p className="text-muted-foreground">
            The resource you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  const siteUrl = window.location.origin;
  const pageUrl = `${siteUrl}/library/${item.slug}`;
  const ogImage = item.thumbnail_url || `${siteUrl}/og-default.png`;

  return (
    <div className="min-h-screen py-12 px-4 max-w-4xl mx-auto">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{item.meta_title || item.title}</title>
        <meta name="description" content={item.meta_description || item.description?.substring(0, 155)} />
        <meta name="keywords" content={item.keywords?.join(', ') || ''} />
        <link rel="canonical" href={pageUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content={item.type === 'video' ? 'video.other' : 'article'} />
        <meta property="og:title" content={item.og_title || item.title} />
        <meta property="og:description" content={item.og_description || item.description?.substring(0, 200)} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={item.og_title || item.title} />
        <meta name="twitter:description" content={item.og_description || item.description?.substring(0, 200)} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* JSON-LD Structured Data */}
        {item.json_ld && (
          <script type="application/ld+json">
            {JSON.stringify({
              ...item.json_ld,
              url: pageUrl,
              image: ogImage
            })}
          </script>
        )}
      </Helmet>

      {/* Back Button */}
      <Link to="/library">
        <Button variant="ghost" className="mb-8">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Library
        </Button>
      </Link>

      {/* Main Content */}
      <article className="bg-card border border-border rounded-xl p-8 mb-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${hasShowAndTellTag(item.resourceTags) ? 'bg-amber-500/20 text-amber-500' : 'bg-primary/10 text-primary'}`}>
              {getIcon(item.type)}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">
                  {item.type}
                </Badge>
                {hasShowAndTellTag(item.resourceTags) && (
                  <Badge className="bg-amber-500 text-amber-950 hover:bg-amber-400 flex items-center gap-1">
                    <Video className="h-3 w-3" />
                    Show & Tell
                  </Badge>
                )}
              </div>
              {(() => {
                // Check for parentheses format: "Name (Subtitle)"
                const parenMatch = item.title.match(/^([^(]+)\s*\(([^)]+)\)$/);
                const colonMatch = !parenMatch ? item.title.match(/^([^:]+):\s*(.+)$/) : null;
                const mainTitle = parenMatch ? parenMatch[1].trim() : colonMatch ? colonMatch[1].trim() : item.title;
                const subtitle = parenMatch ? parenMatch[2].trim() : colonMatch ? colonMatch[2].trim() : null;
                return (
                  <>
                    <h1 className="text-3xl font-bold font-display">{mainTitle}</h1>
                    {subtitle && (
                      <p className="text-lg text-muted-foreground mt-1">{subtitle}</p>
                    )}
                  </>
                );
              })()}
              
              {/* Last Updated + Report Button */}
              <div className="flex items-center gap-4 mt-2 flex-wrap">
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>Last updated: {formatDate(item.updated_at)}</span>
                </div>
                
                <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-1.5 h-auto py-1 px-2">
                      <AlertCircle className="h-3.5 w-3.5" />
                      <span className="text-xs">Report outdated content</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Report Outdated Content</DialogTitle>
                      <DialogDescription>
                        Help us keep our library up to date by reporting content that may be outdated or inaccurate.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="bg-muted/50 rounded-lg p-3 text-sm">
                        <span className="font-medium">Resource:</span> {item.title}
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">
                          What's outdated? (optional)
                        </label>
                        <Textarea
                          placeholder="e.g., The pricing information is no longer accurate, or the features list has changed..."
                          value={reportContext}
                          onChange={(e) => setReportContext(e.target.value)}
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleReportOutdated} disabled={submittingReport}>
                        {submittingReport ? 'Submitting...' : 'Submit Report'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className={`prose prose-lg max-w-none mb-8 ${isLightTheme ? 'prose-slate' : 'prose-invert'}`}>
          <ReactMarkdown>{item.description}</ReactMarkdown>
        </div>

        {/* Rich Content Sections */}
        {item.rich_content && (
          <div className="mb-8">
            <RichContentSection richContent={item.rich_content as RichContent} />
          </div>
        )}

        {/* Metadata Section */}
        <div className="border-t border-border pt-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Created Date */}
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Published</p>
                <p className="text-sm text-muted-foreground">{formatDate(item.created_at)}</p>
              </div>
            </div>

            {/* Phase */}
            {item.phase && (
              <div className="flex items-start gap-3">
                <TagIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Phase</p>
                  <Badge variant="secondary" className="mt-1">{item.phase}</Badge>
                </div>
              </div>
            )}

            {/* Cause */}
            {item.cause && (
              <div className="flex items-start gap-3">
                <TagIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Cause</p>
                  <Badge variant="secondary" className="mt-1">{item.cause}</Badge>
                </div>
              </div>
            )}

            {/* Audience Type */}
            {item.audience_type && (
              <div className="flex items-start gap-3">
                <TagIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Audience Level</p>
                  <Badge variant="secondary" className="mt-1">{item.audience_type}</Badge>
                </div>
              </div>
            )}

            {/* Resource Tags (from database) */}
            {item.resourceTags && item.resourceTags.length > 0 && (
              <div className="flex items-start gap-3 md:col-span-2">
                <TagIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium mb-2">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {item.resourceTags.map((tag) => (
                      <Link
                        key={tag.id}
                        to="/library"
                        onClick={() => {
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent("executeLibraryFilter", { 
                              detail: { tagName: tag.name } 
                            }));
                          }, 300);
                        }}
                      >
                        <Badge variant="outline" className="cursor-pointer hover:bg-primary/10 hover:border-primary transition-colors">
                          {tag.name}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Keywords */}
            {item.keywords && item.keywords.length > 0 && (
              <div className="flex items-start gap-3 md:col-span-2">
                <TagIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium mb-2">Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {item.keywords.slice(0, 10).map((keyword, idx) => (
                      <Badge key={idx} variant="secondary">
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tool Experts Section (for tool-type resources) */}
        {item.type === 'tool' && (
          <div className="mb-8">
            <ToolExpertsSection 
              libraryItemId={item.id} 
              libraryItemTitle={item.title} 
            />
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {/* External Link */}
          <a 
            href={item.content_url} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button className="w-full md:w-auto" size="lg">
              View Resource
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </a>

          {/* View Original File Button(s) */}
          {item.source_files && item.source_files.length > 0 && (
            <>
              {item.source_files.map((filePath, idx) => {
                const fileName = filePath.split('/').pop() || `File ${idx + 1}`;
                return (
                  <Button
                    key={filePath}
                    variant="outline"
                    size="lg"
                    disabled={loadingFile === filePath}
                    onClick={async () => {
                      setLoadingFile(filePath);
                      try {
                        const { data, error } = await supabase.storage
                          .from('pending-resources')
                          .createSignedUrl(filePath, 3600); // 1 hour expiry
                        
                        if (error) throw error;
                        if (data?.signedUrl) {
                          window.open(data.signedUrl, '_blank');
                        }
                      } catch (err) {
                        console.error('Error getting signed URL:', err);
                        toast.error('Failed to open file. Please try again.');
                      } finally {
                        setLoadingFile(null);
                      }
                    }}
                  >
                    {loadingFile === filePath ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    {item.source_files!.length === 1 ? 'View Original File' : fileName}
                  </Button>
                );
              })}
            </>
          )}
        </div>
      </article>

      {/* Similar Content (Vector-based) */}
      {similarItems.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-5 w-5 text-primary" />
            <h2 className="text-2xl font-bold">Similar Resources</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {similarItems.map(similarItem => (
              <Link
                key={similarItem.id}
                to={`/library/${similarItem.slug}`}
                className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-primary/10 text-primary rounded-lg">
                    {getIcon(similarItem.type)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{similarItem.type}</Badge>
                    <Badge variant="outline" className="text-xs">
                      {Math.round(similarItem.similarity * 100)}% match
                    </Badge>
                  </div>
                </div>
                <h3 className="font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                  {similarItem.title}
                </h3>
                <div className={`text-sm text-muted-foreground line-clamp-2 prose prose-sm max-w-none ${isLightTheme ? 'prose-slate' : 'prose-invert'}`}>
                  <ReactMarkdown>{similarItem.description?.split('\n')[0] || ''}</ReactMarkdown>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Related Content (Tag-based fallback) */}
      {relatedItems.length > 0 && similarItems.length === 0 && (
        <section>
          <h2 className="text-2xl font-bold mb-6">Related Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedItems.map(relatedItem => (
              <Link
                key={relatedItem.id}
                to={`/library/${relatedItem.slug}`}
                className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2 bg-primary/10 text-primary rounded-lg">
                    {getIcon(relatedItem.type)}
                  </div>
                  <Badge variant="secondary">{relatedItem.type}</Badge>
                </div>
                <h3 className="font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                  {relatedItem.title}
                </h3>
                <div className={`text-sm text-muted-foreground line-clamp-2 prose prose-sm max-w-none ${isLightTheme ? 'prose-slate' : 'prose-invert'}`}>
                  <ReactMarkdown>{relatedItem.description?.split('\n')[0] || ''}</ReactMarkdown>
                </div>
                {relatedItem.resourceTags && relatedItem.resourceTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {relatedItem.resourceTags.slice(0, 2).map(tag => (
                      <Badge key={tag.id} variant="outline" className="text-xs">
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default LibraryItemDetail;
